import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CircularProgress, MenuItem, Select, Tooltip } from '@mui/material';
import { getContrastRatio } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import PlayArrowIcon from '@mui/icons-material/PlayArrowRounded';
import EditIcon from '@mui/icons-material/EditOutlined';
import DrawIcon from '@mui/icons-material/DrawOutlined';
import { useDrawingMarkers } from '../VideoDrawing/context';
import CommentIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import StarIcon from '@mui/icons-material/StarOutlineRounded';
import SelectedIcon from '@mui/icons-material/CheckBoxOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { enqueueSnackbar } from 'notistack';
import { CLIP, COMMENT } from '../../enums';
import { deleteComment, getClipsCommentsByClipIds, updateComment, updateDrawingMarker } from '../../api';
import { parseColorToHex, timeLineFormatTime } from '../../helpers';
import { getCommentDate, isVisibleComment } from '../../helpers/comments';
import { ReactComponent as SendIcon } from '../../assets/icons/sendClipsIcon.svg';
import { ContainedButton, OutlinedButton } from '../modules/Buttons';
import Comment from '../modules/VideoComments/Comment';
import CommentFooter from '../modules/VideoComments/CommentFooter';
import { ClipSort, useClipFavourites } from './context';
import { FavouriteFeedback, FavouriteStar } from './Star';
import { AngleLock } from './AngleLock';
import styles from './Favourites.module.scss';

const ClipComments = ({ clipId, disabled, onChange }: {
  clipId: string; disabled: boolean; onChange: () => void;
}) => {
  const { feedId } = useClipFavourites();
  const [comments, setComments] = useState<COMMENT[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getClipsCommentsByClipIds([Number(clipId)]).then(({ data }) => {
      if (!cancelled) setComments(data.filter((comment: COMMENT) => comment?.id != null)
        .sort((a: COMMENT, b: COMMENT) => Date.parse(getCommentDate(a)) - Date.parse(getCommentDate(b))));
    }).catch(() => {
      if (!cancelled) setError(true);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [clipId, revision]);

  const refresh = () => { setRevision((value) => value + 1); onChange(); };
  const remove = async (comment: COMMENT) => {
    try { await deleteComment(comment.id, feedId); refresh(); }
    catch { enqueueSnackbar('Could not delete comment.', { variant: 'error' }); }
  };
  const edit = async (comment: COMMENT, newMessage: string) => {
    try {
      if (comment.drawing) await updateDrawingMarker(comment.id, { message: newMessage });
      else await updateComment(comment.id, { newMessage });
      refresh();
    }
    catch { enqueueSnackbar('Could not edit comment.', { variant: 'error' }); }
  };

  const visibleComments = comments.filter((comment) => isVisibleComment(comment));

  return (
    <div className={styles.comments} data-clip-comments id={`favourite-clip-comments-${clipId}`}>
      {loading ? <CircularProgress size={18} /> : error ? (
        <OutlinedButton onClick={() => setRevision((value) => value + 1)}>Retry loading comments</OutlinedButton>
      ) : visibleComments.length ? visibleComments.map((comment) => (
        <Comment key={comment.id} comment={comment} videofeedId={feedId} disable={disabled}
          fetch={refresh} onDelete={remove} onEditSubmit={edit} />
      )) : <p className={styles.hint}>No comments yet.</p>}
      <CommentFooter key={clipId} clipId={clipId} videofeedId={feedId} fetch={refresh} disable={disabled} />
    </div>
  );
};

export const FavouritesPanel = ({ disableComments, onCommentsChange, selectedClips, playhead }: {
  disableComments: boolean;
  onCommentsChange: () => void;
  selectedClips?: CLIP[];
  playhead?: { rowIndex: number; clipIndex: number; seconds: number; playing: boolean };
}) => {
  const { markers: drawingMarkers, showMarker } = useDrawingMarkers();
  const { lists, sorts, setSort, hideSent, setHideSent, sentCount, editor, actions, playerSeek, loading, error, reload, pending, workspace, ids, setFavourites, selection } = useClipFavourites();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sidebarSelection, setSidebarSelection] = useState<Set<string>>(new Set());
  const listRef = useRef<HTMLUListElement>(null);
  const activeClipRef = useRef<HTMLButtonElement>(null);
  const isSelection = selectedClips !== undefined;
  const list = isSelection ? 'selected' : 'favourites';
  const clips = lists[list];
  const selectedIds = isSelection ? sidebarSelection : new Set(selection.map((clip) => String(clip.id)));
  const chosenClips = clips.filter((clip) => selectedIds.has(String(clip.id)));
  const actionClips = chosenClips.length ? chosenClips : clips;
  const allSentHidden = !isSelection && hideSent && sentCount > 0 && !clips.length;
  const sendLabel = `Send ${actionClips.length} ${actionClips.length === 1 ? 'clip' : 'clips'} to composer`;
  const favouritingSelection = isSelection && actionClips.some((clip) => pending.has(String(clip.id)));
  const allFavourited = actionClips.length > 0 && actionClips.every((clip) => ids.has(String(clip.id)));
  const favouriteLabel = `${allFavourited ? 'Unfavourite' : 'Favourite'} ${chosenClips.length || 'all'}`;
  const totalDuration = clips.reduce((total, clip) => total + Math.max(0, clip.time_end - clip.time_start), 0);
  const followPlayhead = isSelection || workspace === 'sidebar';
  const currentClip = followPlayhead && playhead ? clips.find((clip) =>
    playhead.rowIndex === clip.rowIndex && playhead.clipIndex === clip.clipIndex &&
    playhead.seconds >= clip.time_start && playhead.seconds <= clip.time_end) : undefined;

  useEffect(() => {
    if (!isSelection) return;
    const clipIds = new Set(clips.map((clip) => String(clip.id)));
    setSidebarSelection((previous) => {
      const next = new Set(Array.from(previous).filter((id) => clipIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [clips, isSelection]);

  useLayoutEffect(() => {
    const container = listRef.current;
    const activeClip = activeClipRef.current;
    if (!container || !activeClip || !container.clientHeight) return;
    const listBounds = container.getBoundingClientRect();
    const clipBounds = activeClip.getBoundingClientRect();
    const top = listBounds.top + container.clientTop + 8;
    const bottom = listBounds.top + container.clientTop + container.clientHeight - 8;
    if (clipBounds.top < top) container.scrollTop += clipBounds.top - top;
    else if (clipBounds.bottom > bottom) container.scrollTop += clipBounds.bottom - bottom;
  }, [currentClip?.id, clips]);

  if (!isSelection && loading) return <div className={styles.empty}><CircularProgress size={24} aria-label="Loading favourites" /></div>;
  if (!isSelection && error) return <div className={styles.empty}>Could not load favourites.<p><OutlinedButton onClick={reload}>Retry</OutlinedButton></p></div>;

  return (
    <div className={styles.panel}>
      <div className={styles.send}>
        <div className={styles.summary}>
          <span>{clips.length} {clips.length === 1 ? 'clip' : 'clips'}</span>
          <span>{timeLineFormatTime(totalDuration).trim()} total</span>
        </div>
        <div className={styles.sort}>
          <label id={`${list}-sort-label`}>Sort</label>
          <Select
            className={styles.sortSelect}
            size="small"
            IconComponent={ArrowDownIcon}
            MenuProps={{ MenuListProps: { className: styles.sortMenu } }}
            labelId={`${list}-sort-label`}
            value={sorts[list]}
            onKeyDown={(event) => event.stopPropagation()}
            onChange={(event) => setSort(list, event.target.value as ClipSort)}
          >
            <MenuItem value="chronological">Chronological</MenuItem>
            <MenuItem value="row">Row order</MenuItem>
            <MenuItem value="added">{isSelection ? 'Selection order' : 'Date favourited'}</MenuItem>
          </Select>
          {!isSelection && (
            <Tooltip title={`${hideSent ? 'Show' : 'Hide'} clips already sent to a composer`}>
              <button
                type="button"
                className={styles.sentFilter}
                aria-pressed={hideSent}
                onClick={() => setHideSent((previous) => !previous)}
              >
                {hideSent ? <VisibilityIcon /> : <VisibilityOffIcon />}
                <span>{hideSent ? 'Show sent' : 'Hide sent'}</span>
                {sentCount > 0 && <span className={styles.sentCount}>({sentCount})</span>}
              </button>
            </Tooltip>
          )}
        </div>
        {isSelection && clips.length > 0 && <div className={styles.sidebarSelection}>
          <span>{chosenClips.length ? `${chosenClips.length} of ${clips.length} selected` : '⌘ / Ctrl + click to select clips for bulk actions'}</span>
          {chosenClips.length > 0 && <button type="button" aria-label="Clear sidebar selection" onClick={() => setSidebarSelection(new Set())}>Clear</button>}
        </div>}
        <div className={isSelection ? styles.selectionActions : undefined}>
          <ContainedButton className={styles.sendButton} ariaLabel={sendLabel} disabled={!editor.canSend || !actionClips.length || (!isSelection && pending.size > 0)} onClick={() => actions.current?.send(actionClips)}>
            <SendIcon />
            <span className={isSelection ? styles.sendLabel : undefined}>{isSelection ? 'Send to composer' : sendLabel}</span>
            {isSelection && <span className={styles.sendCount} aria-hidden="true">{actionClips.length}</span>}
          </ContainedButton>
          {isSelection && (
            <OutlinedButton
              className={styles.sendButton}
              disabled={loading || error || favouritingSelection || !actionClips.length}
              onClick={() => setFavourites(actionClips.map((clip) => String(clip.id)), !allFavourited)}
            >
              {favouritingSelection ? <CircularProgress size={16} color="inherit" /> : <StarIcon />}
              <span title={`${favouriteLabel} selected clips`}>
                {favouriteLabel}
                <span style={visuallyHidden}> selected clips</span>
              </span>
            </OutlinedButton>
          )}
        </div>
      </div>
      {!clips.length ? (
        <div className={styles.empty}>
          {isSelection ? <SelectedIcon className={styles.emptyIcon} /> : <StarIcon className={styles.emptyIcon} />}
          <strong>{isSelection ? 'No clips selected' : allSentHidden ? 'All favourite clips have been sent' : 'No favourite clips yet'}</strong>
          <p className={styles.hint}>{isSelection ? 'Select clips in the timeline or composer to see them here.' : allSentHidden ? 'Click Show sent to see them again.' : 'Use the star in the toolbar or right-click a clip to add it here.'}</p>
        </div>
      ) : (
        <ul ref={listRef} className={styles.list} aria-label={isSelection ? 'Selected clips' : 'Favourite clips'}
          onClick={(event) => {
            if (!chosenClips.length || (event.target as Element).closest('button, [data-clip-comments]')) return;
            if (isSelection) setSidebarSelection(new Set());
            else actions.current?.deselect(chosenClips);
          }}>
          {clips.map((clip) => {
            const expanded = expandedId === String(clip.id);
            const clipColor = clip.color?.startsWith('#') ? clip.color : parseColorToHex(clip.color || '0.5,0.5,0.5');
            const textColor = getContrastRatio(clipColor, '#0a1929') > getContrastRatio(clipColor, '#fff') ? '#0a1929' : '#fff';
            const duration = Math.max(0, clip.time_end - clip.time_start);
            const isCurrentClip = currentClip?.id === clip.id;
            const isSelected = selectedIds.has(String(clip.id));
            const playheadPosition = followPlayhead && playhead && duration > 0 &&
              playhead.rowIndex === clip.rowIndex && playhead.clipIndex === clip.clipIndex
              ? Math.max(0, Math.min(100, ((playhead.seconds - clip.time_start) / duration) * 100))
              : null;
            const qualifiers = duration > 0
              ? (clip.qualifiers || []).filter((qualifier) => qualifier.time >= clip.time_start && qualifier.time <= clip.time_end)
              : [];
            return (
              <li className={styles.clipItem} key={clip.id}>
                <div className={styles.clipRow}>
                  <div className={styles.clipPreviewWrapper}>
                  <button
                    ref={isCurrentClip ? activeClipRef : undefined}
                    type="button"
                    className={`${styles.clipPreview} ${isCurrentClip ? styles.clipPreviewPlaying : ''} ${isSelected ? styles.clipPreviewSelected : ''}`}
                    style={{ '--clip-color': clipColor, color: textColor } as React.CSSProperties}
                    aria-label={`Play ${clip.topicName}, clip ${clip.clipIndex + 1}`}
                    aria-pressed={isSelected}
                    onClick={(event) => {
                      if (isSelection && (event.metaKey || event.ctrlKey)) {
                        event.preventDefault();
                        event.stopPropagation();
                        setSidebarSelection((previous) => {
                          const next = new Set(previous);
                          const id = String(clip.id);
                          if (next.has(id)) next.delete(id);
                          else next.add(id);
                          return next;
                        });
                        return;
                      }
                      if (!isSelection) {
                        const additive = event.metaKey || event.ctrlKey;
                        if (additive && isSelected) {
                          actions.current?.deselect(clip);
                          return;
                        }
                        if (!additive || !isSelected) actions.current?.select(clip, additive);
                      }
                      actions.current?.seek(clip, true);
                      playerSeek.current?.(clip.time_start);
                    }}
                  >
                    <FavouriteFeedback clipId={clip.id} />
                    {isSelected ? <SelectedIcon /> : <PlayArrowIcon />}
                    <span className={styles.clipLabel}>
                      <span className={styles.clipTitle} title={clip.topicName}>{clip.topicName}</span>
                      <span className={styles.clipNumber}>Clip {clip.clipIndex + 1}<AngleLock clip={clip} inline /></span>
                    </span>
                    <span className={styles.clipDuration} title="Duration">
                      {timeLineFormatTime(duration).trim()}
                    </span>
                    <span className={styles.qualifierTrack}>
                      {qualifiers.map((qualifier) => (
                        <Tooltip key={qualifier.id} title={qualifier.name} disableInteractive>
                          <span
                            className={styles.qualifier}
                            style={{
                              left: `${((qualifier.time - clip.time_start) / duration) * 100}%`,
                              backgroundColor: qualifier.color?.startsWith('#') ? qualifier.color : parseColorToHex(qualifier.color),
                            }}
                          />
                        </Tooltip>
                      ))}
                    </span>
                    {playheadPosition !== null && (
                      <span className={styles.playheadTrack} aria-hidden="true">
                        <span className={styles.playhead} style={{ left: `${playheadPosition}%` }} />
                      </span>
                    )}
                  </button>
                  {duration > 0 && drawingMarkers.filter((marker) => String(marker.clip_id) === String(clip.id) &&
                    marker.timestamp >= clip.time_start && marker.timestamp <= clip.time_end).map((marker) => (
                    <Tooltip key={marker.id} title={`Drawing · ${timeLineFormatTime(marker.timestamp)}`}>
                      <button type="button" className={styles.drawingMarker}
                        aria-label={`Drawing at ${timeLineFormatTime(marker.timestamp)}`}
                        style={{ left: `clamp(0px, ${(marker.timestamp - clip.time_start) / duration * 100}%, calc(100% - 16px))` }}
                        onClick={(event) => { event.stopPropagation(); showMarker(marker); }}><DrawIcon /></button>
                    </Tooltip>
                  ))}
                  </div>
                  <FavouriteStar clipId={clip.id} />
                </div>
                <div className={styles.clipMeta}>
                  <Tooltip title="Jump to clip">
                    <button
                      type="button"
                      className={styles.clipRange}
                      aria-label={`Jump to ${clip.topicName}, clip ${clip.clipIndex + 1}`}
                      onClick={() => {
                        actions.current?.seek(clip, playhead?.playing ?? false);
                        playerSeek.current?.(clip.time_start);
                      }}
                    >
                      <span>{timeLineFormatTime(clip.time_start).trim()}</span>
                      <span className={styles.rangeArrow} aria-hidden="true">→</span>
                      <span>{timeLineFormatTime(clip.time_end).trim()}</span>
                    </button>
                  </Tooltip>
                  <div className={styles.clipActions}>
                    {editor.canEdit && (
                      <Tooltip title="Edit original clip boundaries">
                        <button type="button" className={styles.clipAction} aria-label="Edit original clip boundaries" onClick={() => actions.current?.edit(clip)}>
                          <EditIcon />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip title={expanded ? 'Hide comments' : 'Show comments'}>
                      <button
                        type="button"
                        className={styles.clipAction}
                        aria-label={expanded ? 'Hide comments' : 'Show comments'}
                        aria-expanded={expanded}
                        aria-controls={expanded ? `favourite-clip-comments-${clip.id}` : undefined}
                        onClick={() => setExpandedId(expanded ? null : String(clip.id))}
                      >
                        <CommentIcon />
                      </button>
                    </Tooltip>
                    {isSelection && (
                      <Tooltip title="Remove from selected clips. This does not delete the clip.">
                        <button
                          type="button"
                          className={`${styles.clipAction} ${styles.deselect}`}
                          aria-label={`Deselect ${clip.topicName}, clip ${clip.clipIndex + 1}`}
                          onClick={() => actions.current?.deselect(clip)}
                        >
                          Deselect
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
                {expanded && <ClipComments clipId={String(clip.id)} disabled={disableComments} onChange={onCommentsChange} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
